import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchVendors, createVendor, updateVendor, deleteVendor } from '../../api';
import { useAccess } from '../auth/AccessContext';
import DataGrid from '../common/DataGrid';
import Modal from './Modal';
import { actionsColumn, formatDate, toISODate } from './shared';
import '../reports/Reports.css';
import './Marketing.css';

// Vendor management: the full add / edit / delete surface for marketing
// vendors. A vendor is just a name — campaigns and spends hang off it, so a
// vendor still referenced by campaigns can't be deleted (the server explains
// why, and the grid shows the campaign count up front).
export default function Vendors() {
    // A profile limited to certain vendors sees just those here, and can't
    // add one — a new vendor would be outside its limit by definition (the
    // server refuses it too).
    const { vendorScope } = useAccess();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // The open dialog: { mode: 'create' | 'edit' | 'delete', vendor }.
    const [dialog, setDialog] = useState(null);
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setVendors(await fetchVendors() || []);
        } catch (err) {
            setError(err.message);
            setVendors([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setName('');
        setDialogError(null);
        setDialog({ mode: 'create' });
    };

    const openEdit = useCallback((vendor) => {
        setName(vendor.name);
        setDialogError(null);
        setDialog({ mode: 'edit', vendor });
    }, []);

    const openDelete = useCallback((vendor) => {
        setDialogError(null);
        setDialog({ mode: 'delete', vendor });
    }, []);

    const closeDialog = () => { setDialog(null); setDialogError(null); };

    const handleSubmit = async () => {
        setSaving(true);
        setDialogError(null);
        try {
            if (dialog.mode === 'create') await createVendor(name.trim());
            else if (dialog.mode === 'edit') await updateVendor(dialog.vendor.id, name.trim());
            else await deleteVendor(dialog.vendor.id);
            setDialog(null);
            await load();
        } catch (err) {
            setDialogError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const columnDefs = useMemo(() => [
        { field: 'name', headerName: 'Vendor', flex: 1, minWidth: 200 },
        { field: 'campaigns', headerName: 'Campaigns', type: 'rightAligned', width: 130, filter: 'agNumberColumnFilter' },
        { field: 'created_at', headerName: 'Created', width: 140, valueFormatter: (p) => formatDate(p.value) },
        actionsColumn(openEdit, openDelete),
    ], [openEdit, openDelete]);

    const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, minWidth: 120 }), []);

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Vendors</h1>
                    <p className="page__subtitle">
                        {vendorScope.scoped
                            ? 'The marketing vendors your profile covers. Rename them here.'
                            : 'The marketing vendors campaigns are bought from. Add, rename, or remove them here.'}
                    </p>
                </div>
                {!vendorScope.scoped && (
                    <div className="mkt__header-actions">
                        <button className="blueButton" onClick={openCreate}>+ Add Vendor</button>
                    </div>
                )}
            </div>

            {error && <p className="mkt__error" style={{ marginTop: 16 }}>{error}</p>}

            {loading && <p className="page__subtitle">Loading vendors…</p>}

            {!loading && !error && vendors.length === 0 && (
                <p className="page__subtitle" style={{ marginTop: 16 }}>
                    {vendorScope.scoped
                        ? 'Your profile isn\'t limited to any vendors yet. Ask an admin to add one.'
                        : 'No vendors yet. Add one to start creating campaigns.'}
                </p>
            )}

            {vendors.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <DataGrid
                        rowData={vendors}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        height="calc(100vh - 260px)"
                        enableExport
                        exportFileName={() => `Vendors_${toISODate(new Date())}`}
                    />
                </div>
            )}

            {dialog && dialog.mode !== 'delete' && (
                <Modal
                    title={dialog.mode === 'create' ? 'Add Vendor' : 'Edit Vendor'}
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel={dialog.mode === 'create' ? 'Add Vendor' : 'Save Changes'}
                    submitting={saving}
                    submitDisabled={!name.trim()}
                    error={dialogError}
                >
                    <label className="mkt__field">
                        <span>Vendor Name</span>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Google Ads"
                            autoFocus
                        />
                    </label>
                </Modal>
            )}

            {dialog && dialog.mode === 'delete' && (
                <Modal
                    title="Delete Vendor"
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel="Delete Vendor"
                    submitVariant="danger"
                    submitting={saving}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Delete <strong>{dialog.vendor.name}</strong>? This can't be undone.
                    </p>
                    {dialog.vendor.campaigns > 0 && (
                        <p className="mkt-modal__text">
                            It has {dialog.vendor.campaigns} campaign{dialog.vendor.campaigns === 1 ? '' : 's'}, so
                            it can't be deleted until {dialog.vendor.campaigns === 1 ? 'that campaign is' : 'those campaigns are'} moved or removed.
                        </p>
                    )}
                </Modal>
            )}
        </div>
    );
}
